import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Activity, Database, Mail, Network, ShieldCheck, ArrowRight, Focus, Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

const UseCasesPage = () => {
    const pageRef = useRef(null);
    const gridRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        let ctx = gsap.context(() => {
            gsap.fromTo('.reveal-up',
                { y: 40, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.8,
                    stagger: 0.1,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: pageRef.current,
                        start: 'top top+=100',
                    }
                }
            );

            gsap.fromTo(gsap.utils.toArray('.use-case-card'),
                { y: 30, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.6,
                    stagger: 0.15,
                    ease: 'power2.out',
                    scrollTrigger: {
                        trigger: gridRef.current,
                        start: 'top 85%',
                    }
                }
            );
        }, pageRef);
        return () => ctx.revert();
    }, []);

    const useCases = [
        {
            title: "Pipeline Intelligence",
            icon: <Database className="w-6 h-6 text-[#7B61FF]" />,
            image: "/images/generated/use_case_pipeline_1771658576403.png",
            description: "Automatically extracts context from sales calls (Avoma/Zoom), maps properties, and drives CRM updates without manual data entry. Identifies stalled deals and prompts action.",
            featureList: ["Automated CRM Syncing", "Meeting Transcript Parsing", "Follow-up Drafting"]
        },
        {
            title: "The Accountability Monitor",
            icon: <Activity className="w-6 h-6 text-[#10B981]" />,
            image: "/images/generated/use_case_accountability_1771658591678.png",
            description: "Your local server runs a heartbeat query against your project management software (ClickUp/Jira). If a ticket sits in 'Ready' for 48 hours, the agent automatically surfaces it in your Slack.",
            featureList: ["Cron Job Execution", "Slack/Telegram Direct Integration", "Task Lifecycle Management"]
        },
        {
            title: "The Communications Proxy",
            icon: <Mail className="w-6 h-6 text-[#F59E0B]" />,
            image: "/images/generated/use_case_proxy_1771658607629.png",
            description: "Operates a 'Dirty Data' infrastructure Gmail account. Vendors and software alerts route here; the agent reads, categorizes, summarizes, and forwards only critical events to your primary inbox.",
            featureList: ["Prompt Injection Defense", "Inbox Zero Automation", "Contextual Summarization"]
        },
        {
            title: "Sub-Agent Orchestration",
            icon: <Network className="w-6 h-6 text-[#3B82F6]" />,
            image: "/images/generated/use_case_orchestration_1771658622784.png",
            description: "Acts as the 'Steward' in a Bicameral Mind setup. You delegate a multi-step objective to OpenClaw, which then writes a JSON payload specification to spin up a specialized 'Builder' agent for execution.",
            featureList: ["JSON Payload Hand-offs", "Multi-Agent Workflows", "Strict Role Enforcement"]
        }
    ];

    return (
        <div ref={pageRef} className="pt-32 pb-24 bg-[#0A0A14] text-[#F0EFF4] min-h-screen selection:bg-[#7B61FF] selection:text-[#F0EFF4] font-sans">

            {/* Header */}
            <section className="max-w-4xl mx-auto px-6 mb-24 text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#18181B] border border-[#F0EFF4]/10 text-xs font-semibold tracking-wider text-[#7B61FF] mb-6 reveal-up">
                    <Focus className="w-4 h-4" />
                    APPLIED AUTONOMY
                </div>
                <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 reveal-up">
                    What does an always-on<br />
                    <span className="font-drama italic text-[#7B61FF] font-normal tracking-normal text-5xl md:text-7xl">agent actually do?</span>
                </h1>
                <p className="text-lg md:text-xl text-[#F0EFF4]/60 leading-relaxed max-w-2xl mx-auto reveal-up">
                    OpenClaw is industry-agnostic. It is simply a highly secure, persistent bridge between a top-tier LLM and your digital ecosystem. Here is how top executives deploy it.
                </p>
            </section>

            {/* Grid */}
            <section className="max-w-7xl mx-auto px-6 mb-32">
                <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 gap-8 use-case-grid">
                    {useCases.map((useCase, index) => (
                        <div key={index} className="use-case-card bg-[#18181B] border border-[#F0EFF4]/10 rounded-[2rem] overflow-hidden group hover:-translate-y-2 hover:scale-[1.03] transition-all duration-500 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] hover:border-[#7B61FF]/40 hover:shadow-[0_0_40px_rgba(123,97,255,0.15)] flex flex-col">

                            {/* Image Header */}
                            <div className="w-full h-48 md:h-56 overflow-hidden relative border-b border-[#F0EFF4]/10">
                                <img src={useCase.image} alt={useCase.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#18181B] via-transparent to-transparent"></div>

                                <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity duration-500 transform group-hover:scale-[3] origin-top-right">
                                    {useCase.icon}
                                </div>

                                <div className="absolute bottom-4 left-6 md:left-8 bg-[#0A0A14]/80 backdrop-blur-md w-14 h-14 rounded-xl flex items-center justify-center border border-[#F0EFF4]/20 shadow-[0_4px_20px_rgba(0,0,0,0.5)]">
                                    {useCase.icon}
                                </div>
                            </div>

                            {/* Content Block */}
                            <div className="p-8 md:p-10 flex-1 flex flex-col">
                                <h3 className="text-2xl font-bold mb-4">{useCase.title}</h3>
                                <p className="text-[#F0EFF4]/60 mb-8 leading-relaxed flex-1">
                                    {useCase.description}
                                </p>

                                <ul className="space-y-3 border-t border-[#F0EFF4]/10 pt-6">
                                    {useCase.featureList.map((feature, i) => (
                                        <li key={i} className="flex items-center gap-3 text-sm font-data text-[#F0EFF4]/80">
                                            <Briefcase className="w-4 h-4 text-[#7B61FF]/60" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Integration Note */}
            <section className="max-w-4xl mx-auto px-6 mb-32 text-center reveal-up">
                <div className="bg-gradient-to-b from-[#18181B] to-[#0A0A14] border border-[#F0EFF4]/10 rounded-[2rem] p-10 md:p-16">
                    <ShieldCheck className="w-12 h-12 text-[#10B981] mx-auto mb-6" />
                    <h3 className="text-3xl font-bold mb-4">You Retain Total Control</h3>
                    <p className="text-[#F0EFF4]/60 max-w-2xl mx-auto leading-relaxed">
                        By default, OpenClaw operates with \`exec.ask: "on"\`. This means for any high-risk tactical execution (pushing code to production, sending external emails), the agent pauses and surfaces a direct request to your private Slack or Telegram channel. Nothing happens without your explicit approval until you graduate the agent to full autonomy.
                    </p>
                </div>
            </section>

            {/* CTA */}
            <section className="text-center reveal-up pb-12">
                <Link to="/#reserve" className="inline-flex items-center gap-2 bg-[#7B61FF] text-[#F0EFF4] px-8 py-4 rounded-full font-bold hover:-translate-y-1 hover:scale-[1.03] transition-all duration-500 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] shadow-[0_0_30px_rgba(123,97,255,0.4)] hover:shadow-[0_0_50px_rgba(123,97,255,0.6)]">
                    Start Orchestrating
                    <ArrowRight className="w-5 h-5" />
                </Link>
            </section>
        </div>
    );
};

export default UseCasesPage;
